import { getCurrentLanguageCode, getSimpleLanguageCode } from "./helpers";
import {
  LANGUAGE_EN_US_CODE,
  LANGUAGE_FR_CODE,
  PELICAN_CHECKOUT_COOKIES_CA,
  PELICAN_CHECKOUT_COOKIES_US,
} from "../constants/AppConstants";

/**
 * Get language and region configuration
 * @returns {Object} Configuration object with language, country, and cookies
 */
export const getLanguageConfig = () => {
  const currentLanguage = getCurrentLanguageCode(LANGUAGE_FR_CODE);
  const countryCode = currentLanguage === LANGUAGE_EN_US_CODE ? "US" : "CA";
  const languageCode = getSimpleLanguageCode(LANGUAGE_FR_CODE);
  const cookiesName =
    currentLanguage === LANGUAGE_EN_US_CODE
      ? PELICAN_CHECKOUT_COOKIES_US
      : PELICAN_CHECKOUT_COOKIES_CA;

  return {
    currentLanguage,
    countryCode,
    languageCode,
    cookiesName,
  };
};

/**
 * Get cart creation input with buyer identity
 * @param {string} countryCode - Country code
 * @param {string} customerAccessToken - Optional customer access token
 * @returns {Object} Cart input object
 */
export const getCartCreateInput = (countryCode, customerAccessToken = null) => {
  return {
    buyerIdentity: {
      countryCode,
      ...(customerAccessToken && { customerAccessToken }),
    },
  };
};

/**
 * Get standard headers for Shopify requests
 * @param {string} storefrontAccessToken - Storefront access token
 * @returns {Object} Headers object
 */
export const getShopifyHeaders = storefrontAccessToken => {
  return {
    "Content-Type": "application/json",
    "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
  };
};

/**
 * Handle Shopify API response with error checking
 * @param {Response} response - Fetch response
 * @returns {Promise} Parsed JSON response
 */
export const handleShopifyResponse = async response => {
  if (!response.ok) {
    throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * Check for GraphQL or user errors in response
 * @param {Object} data - Response data
 * @param {Array} errors - GraphQL errors
 * @param {string} operation - Operation name for logging
 * @returns {Object|null} Returns data if no errors, null if errors found
 */
export const checkForErrors = (data, errors, operation) => {
  if (errors && errors.length > 0) {
    console.error(`${operation} GraphQL errors:`, errors);
    return null;
  }

  const operationData = data?.[operation];
  if (operationData?.userErrors?.length > 0) {
    console.error(`${operation} user errors:`, operationData.userErrors);
    return null;
  }

  return operationData;
};

/**
 * Check if cart has line items
 * @param {Object} cart - Cart object
 * @returns {boolean} True if cart has items
 */
export const cartHasItems = cart => {
  return cart?.lines?.edges?.length > 0 || cart?.lineItems?.length > 0;
};

/**
 * Generic Shopify fetch wrapper with error handling
 * @param {Object} client - Shopify client
 * @param {string} query - GraphQL query/mutation
 * @param {Object} variables - Query variables
 * @param {Object} headers - Request headers
 * @returns {Promise} Response data
 */
export const shopifyFetch = async (client, query, variables = {}, headers = {}) => {
  const response = await client.fetch(query, {
    variables,
    headers,
  });

  return handleShopifyResponse(response);
};
